/**
 * HiddenColumnsButton — Minimal-Toolbar-Button im Stundenkonto.
 *
 * Ersetzt die alte 3-fach-Toolbar (Ansicht/Spalten/Kompakt) durch einen einzigen
 * dezenten Button: erscheint NUR wenn mind. eine Spalte ausgeblendet wurde.
 *
 * Klick -> kleines Dropdown mit allen ausgeblendeten Spalten zum Wieder-Einblenden.
 *
 * UX-Ziel (07.02.2026, User-Wunsch):
 *  - Spalten-Hide passiert direkt im Header (Auge-Icon pro Spalte) -> bereits implementiert
 *  - Wieder-Einblenden ueber diese kleine Restore-Liste
 *  - keine grossen Toolbar-Buttons mehr -> ruhigere UI, weniger Click-Targets
 */
import { useState, useRef, useEffect } from "react";
import { Eye, Plus } from "lucide-react";

export default function HiddenColumnsButton({ allColumns, prefs, isNachunternehmer }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Spalten die der Nachunternehmer nie sehen darf (gesperrt)
  const lockedHidden = isNachunternehmer
    ? new Set(["fhkm", "fhz", "gh", "frkm", "frz", "gr", "soll", "diff"])
    : new Set();

  const hidden = allColumns.filter(
    (c) => !prefs.visible.has(c.key) && !lockedHidden.has(c.key) && c.key !== "wt" && c.key !== "act"
  );

  if (hidden.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 px-2.5 py-1.5 text-[12px] font-medium rounded transition-colors"
        style={{
          background: "var(--tga-bg-content)",
          border: "1px solid var(--tga-border)",
          color: "var(--tga-text-secondary)",
        }}
        title={`${hidden.length} Spalte(n) ausgeblendet`}
        data-testid="hidden-columns-btn"
      >
        <Plus className="w-3.5 h-3.5" />
        Spalten ({hidden.length})
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-56 rounded-lg shadow-lg z-50 overflow-hidden max-h-[420px] overflow-y-auto"
          style={{
            background: "var(--tga-bg-elevated)",
            border: "1px solid var(--tga-border-strong)",
          }}
          data-testid="hidden-columns-menu"
        >
          <div
            className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold sticky top-0"
            style={{
              color: "var(--tga-text-dim)",
              background: "var(--tga-bg-elevated)",
              borderBottom: "1px solid var(--tga-border)",
            }}
          >
            Ausgeblendete Spalten
          </div>
          {hidden.map((col) => (
            <button
              key={col.key}
              onClick={() => {
                prefs.toggleColumn(col.key);
                if (hidden.length === 1) setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-left transition-colors"
              style={{ color: "var(--tga-text)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--tga-bg-hover-soft)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              data-testid={`restore-col-${col.key}`}
            >
              <Eye className="w-3 h-3" style={{ color: "var(--tga-green)" }} />
              <span className="flex-1">{col.label || col.key}</span>
              <span className="text-[10px]" style={{ color: "var(--tga-text-dim)" }}>einblenden</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
